import {RankedConstants} from '../../../../shared/GameEnums.ts'
import {BotsMatchType, MatchmakingMatchTypes, MatchType, MODE_BASE_PLAYTIME_MINUTES} from '@colonist/game-base-types'
import {ClientUserState} from '../ClientUserState.ts'
import {ViewPopupLogin} from '../ui_popup/ui_popup_membership_incentive/ViewPopupLogin.ts'
import {ViewPopupBecomeMemberGameplay} from '../ui_popup/ViewPopupBecomeMemberGameplay.ts'
import {AnalyticEventCategory, AnalyticEventType, AnalyticEventValue} from '../../../../shared/entity_datas/EntityDatas.ts'
import {UserSessionUtils} from '../UserSessionUtils.ts'
import {ErrorsMatchmaking} from '../../../../shared/errors/ErrorsMatchmaking.ts'
import {BaseError} from '../../../../shared/errors/BaseError.ts'
import {TranslationObject, getNestedTranslation} from '@colonist/i18n'
import {ViewPopup} from '../ui_popup/ViewPopup.ts'
import {uiManagerEvents} from '../EntryShared.ts'
import {CK_FREE_GAME_COUNT, getCKRankedState, CKRankedState, shouldLockRankedCitiesAndKnights} from '../../../../shared/matchmaking/MatchmakingUtils'
import {citiesAndKnightsMatchmakingMatchTypes, rankedMatchmakingMatchTypes} from '../../../../shared/matchmaking/MatchmakingEnums'
import {ELOTypeEnum, LobbyImage} from '@colonist/types'
import {FindGameRankedState} from '../../../../shared/ranked/ranked_user_state_data/FindGameRankedState'
import {RankedMMRState, RankedPlacementState, RankedUserStateController, RankedUserStateType} from '@colonist/leaderboard-types'
import {CA} from '../analytics/Analytics'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {UIImagePathController} from '../UIEnums.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {ShuffleQueueData} from '../../../../shared/shuffle_queue/ShuffleInterfaces.ts'
import {getShuffleQueueModeName, getShuffleQueueMapName, getShuffleQueuePlayTimeForPlayerCount} from './ShuffleQueueUtils'
import {BadgeData} from '../ui_html/home_page/UIProgressBadgeElement'
import {SocketLobbySend} from '../socket/SocketLobbySend.ts'
import {ClientShuffleQueueController} from '../shuffle/ClientShuffleQueueController.ts'
import {TimeUtils} from '@colonist/utils'

const rankedMatchTypeToEloTypeMap: Partial<Record<MatchmakingMatchTypes, ELOTypeEnum>> = {
    [MatchmakingMatchTypes.RankedClassic1v1]: ELOTypeEnum.VisibleRanked1v1,
    [MatchmakingMatchTypes.RankedClassic4P]: ELOTypeEnum.VisibleRanked4PClassic,
    [MatchmakingMatchTypes.RankedCitiesAndKnights4P]: ELOTypeEnum.VisibleRankedCitiesAndKnights4P,
}

export interface RankedMatchTypeData {
    description: TranslationObject | undefined
    badge: BadgeData | undefined
}

export function getMatchTypePlayTime(matchType: MatchType): number | undefined {
    switch(matchType) {
        case BotsMatchType.BeginnerMode:
            return MODE_BASE_PLAYTIME_MINUTES.BOTS_BEGINNER_MODE
        case BotsMatchType.ColonistRush:
            return MODE_BASE_PLAYTIME_MINUTES.BOTS_COLONIST_RUSH
        case BotsMatchType.Easy:
        case BotsMatchType.Medium:
        case BotsMatchType.Hard:
            return MODE_BASE_PLAYTIME_MINUTES.BOTS_STANDARD
        case MatchmakingMatchTypes.UnrankedClassic1v1:
        case MatchmakingMatchTypes.RankedClassic1v1:
            return MODE_BASE_PLAYTIME_MINUTES.CLASSIC_1V1
        case MatchmakingMatchTypes.UnrankedClassic4P:
            return 30
        case MatchmakingMatchTypes.RankedClassic4P:
            return MODE_BASE_PLAYTIME_MINUTES.CLASSIC
        case MatchmakingMatchTypes.RankedCitiesAndKnights4P:
            return MODE_BASE_PLAYTIME_MINUTES.CITIES_AND_KNIGHTS
        case MatchmakingMatchTypes.UnrankedShuffle:
            return getShuffleQueuePlayTimeForPlayerCount(ClientShuffleQueueController.shuffleQueueData?.playerCount, ClientShuffleQueueController.shuffleQueueData?.modeId)
        default:
            return undefined
    }
}

export function getMatchTypePlayerAndBotCount(matchType: MatchType): {
    playerCount: number
    botCount: number
} {
    switch(matchType) {
        case BotsMatchType.BeginnerMode:
            return {playerCount: 1, botCount: 0}
        case BotsMatchType.ColonistRush:
        case BotsMatchType.Easy:
        case BotsMatchType.Medium:
        case BotsMatchType.Hard:
            return {playerCount: 1, botCount: 3}
        case MatchmakingMatchTypes.UnrankedClassic1v1:
        case MatchmakingMatchTypes.RankedClassic1v1:
            return {playerCount: 2, botCount: 0}
        case MatchmakingMatchTypes.UnrankedClassic4P:
        case MatchmakingMatchTypes.RankedClassic4P:
        case MatchmakingMatchTypes.RankedCitiesAndKnights4P:
            return {playerCount: 4, botCount: 0}
        case MatchmakingMatchTypes.UnrankedShuffle: {
            const shuffleQueueData = ClientShuffleQueueController.shuffleQueueData
            if(shuffleQueueData == undefined) return {playerCount: 0, botCount: 0}
            const totalPlayerCount = shuffleQueueData.playerCount
            if(shuffleQueueData.isBotGame == true) return {playerCount: 1, botCount: totalPlayerCount - 1}
            return {playerCount: totalPlayerCount, botCount: 0}
        }
        default:
            return {playerCount: 0, botCount: 0}
    }
}

export function getMatchTypeEligibilityAndCreatePopup(selectedMatchType: MatchmakingMatchTypes): {
    isEligible: boolean
    createPopup: (() => void) | undefined
    shouldDisableCardIfNotEligible: boolean
} {
    const isRankedMatchTypeSelected = isRankedMatchType(selectedMatchType)
    const isShuffleMatchTypeSelected = selectedMatchType == MatchmakingMatchTypes.UnrankedShuffle
    const eligibleMatchTypeResult = {isEligible: true, createPopup: undefined, shouldDisableCardIfNotEligible: true}

    if(!isRankedMatchTypeSelected && !isShuffleMatchTypeSelected) return eligibleMatchTypeResult

    if(!ClientUserState.userState?.isLoggedIn) return {isEligible: false, createPopup: () => new ViewPopupLogin(), shouldDisableCardIfNotEligible: true}

    if(isShuffleMatchTypeSelected) {
        const requiredGamesForShuffle = RankedConstants.NumberOfGamesToUnlockRanked
        const hasPlayedEnoughCasualGamesForShuffle = ClientUserState.userState?.totalCompletedGameCount >= requiredGamesForShuffle
        if(!hasPlayedEnoughCasualGamesForShuffle) {
            const shuffleLockedError = new ErrorsMatchmaking.ShuffleLocked(ClientUserState.userState.totalCompletedGameCount, requiredGamesForShuffle)
            return {isEligible: false, createPopup: () => showMatchmakingErrorPopup(shuffleLockedError), shouldDisableCardIfNotEligible: true}
        }

        const playerData = ClientShuffleQueueController.shufflePlayerData
        const hasMembership = ClientUserState.userState.items.hasMembership()
        const hasFreeGamesRemaining = playerData != undefined && playerData.totalGameCount < playerData.freeGamesTotalCount
        const hasDailyFreeGame = playerData?.playedToday === false

        if(hasMembership) return eligibleMatchTypeResult
        if(hasFreeGamesRemaining) return eligibleMatchTypeResult
        if(hasDailyFreeGame) return eligibleMatchTypeResult

        return {
            isEligible: false,
            createPopup: () => {
                const checkAction = uiManagerEvents.premiumMembershipCheckAction()
                new ViewPopupBecomeMemberGameplay(checkAction, AnalyticEventValue.PopupMembershipIncentivesShuffleQueue)
            },
            shouldDisableCardIfNotEligible: false,
        }
    }

    const isCitiesAndKnightsMatchTypeSelected = isCitiesAndKnightsMatchType(selectedMatchType)
    const shouldLockCitiesAndKnights = shouldLockRankedCitiesAndKnights(ClientUserState.userState?.items, ClientUserState.userState?.ckTotalGameCount, ClientUserState.abTests, ClientUserState.userState?.ckNextRerollAt, ClientUserState.userState?.ckFreeGameUntil)
    if(isCitiesAndKnightsMatchTypeSelected && shouldLockCitiesAndKnights) {
        return {
            isEligible: false,
            createPopup: () => {
                const checkAction = uiManagerEvents.premiumMembershipCheckAction()
                new ViewPopupBecomeMemberGameplay(checkAction, AnalyticEventValue.PopupMembershipIncentivesRankedCitiesAndKnights)
            },
            shouldDisableCardIfNotEligible: true,
        }
    }

    const isEligibleForRanked = UserSessionUtils.userEligibleForRanked()
    if(isEligibleForRanked) return eligibleMatchTypeResult

    const requiredNumberOfGamesForRanked = RankedConstants.NumberOfGamesToUnlockRanked
    const rankedLockedError = new ErrorsMatchmaking.RankedLocked(ClientUserState.userState.totalCompletedGameCount, requiredNumberOfGamesForRanked)

    return {
        isEligible: false,
        createPopup: () => showMatchmakingErrorPopup(rankedLockedError),
        shouldDisableCardIfNotEligible: true,
    }
}

export function getMatchTypeDescription(selectedMatchType: MatchmakingMatchTypes, findGameRankedStates?: FindGameRankedState[]): TranslationObject {
    const isRankedMatchTypeSelected = isRankedMatchType(selectedMatchType)
    if(!isRankedMatchTypeSelected) return {key: 'strings:homePage.matchmakingPopup.playAgainstSimilarKarma'}

    if(!ClientUserState.userState?.isLoggedIn) return {key: 'strings:homePage.ranked.logInToPlayRanked'}

    const isCitiesAndKnightsMatchTypeSelected = isCitiesAndKnightsMatchType(selectedMatchType)
    const shouldLockCitiesAndKnights = shouldLockRankedCitiesAndKnights(ClientUserState.userState?.items, ClientUserState.userState?.ckTotalGameCount, ClientUserState.abTests, ClientUserState.userState?.ckNextRerollAt, ClientUserState.userState?.ckFreeGameUntil)
    if(isCitiesAndKnightsMatchTypeSelected && shouldLockCitiesAndKnights) return {key: 'strings:homePage.becomeAMemberToUnlock'}

    const isEligibleForRanked = UserSessionUtils.userEligibleForRanked()
    if(!isEligibleForRanked) {
        const requiredNumberOfGamesForRanked = RankedConstants.NumberOfGamesToUnlockRanked
        return {key: 'strings:homePage.ranked.rankedIneligible', options: {count: requiredNumberOfGamesForRanked - ClientUserState.userState.totalCompletedGameCount}}
    }

    return getDescriptionForRankedMatchType(selectedMatchType, findGameRankedStates)
}

export function getMatchTypeDetails(selectedMatchType: MatchmakingMatchTypes, findGameRankedStates?: FindGameRankedState[]): RankedMatchTypeData {
    if(selectedMatchType == MatchmakingMatchTypes.UnrankedShuffle) return getShuffleQueueDetails()

    const isRankedMatchTypeSelected = isRankedMatchType(selectedMatchType)
    if(!isRankedMatchTypeSelected) {
        return {description: undefined, badge: undefined}
    }

    if(!ClientUserState.userState?.isLoggedIn) {
        return {
            description: {key: 'strings:homePage.ranked.logInToPlayRanked'},
            badge: undefined,
        }
    }

    const isCitiesAndKnightsMatchTypeSelected = isCitiesAndKnightsMatchType(selectedMatchType)
    if(isCitiesAndKnightsMatchTypeSelected) {
        const ckRankedCardData = getCKRankedCardData()
        if(ckRankedCardData != undefined) return ckRankedCardData
    }

    const isEligibleForRanked = UserSessionUtils.userEligibleForRanked()
    if(!isEligibleForRanked) {
        const requiredNumberOfGamesForRanked = RankedConstants.NumberOfGamesToUnlockRanked
        const completedGames = ClientUserState.userState.totalCompletedGameCount
        return {
            description: {key: 'strings:homePage.matchmakingSelect.playGamesToUnlockRanked'},
            badge: {
                text: {key: 'strings:colonistCommon.utils.#buffer', options: {value: `${completedGames}/${requiredNumberOfGamesForRanked}`}},
                icon: LobbyImage.IconSwords,
            },
        }
    }

    const expectedEloType = rankedMatchTypeToEloTypeMap[selectedMatchType]
    const findGameRankedState = findGameRankedStates?.find((gameState) => gameState.eloType === expectedEloType)

    if(findGameRankedState?.rankedState != undefined) {
        const rankedState = RankedUserStateController.deserialize(findGameRankedState.rankedState)
        if(rankedState.type == RankedUserStateType.Placement) {
            const placementState = rankedState as RankedPlacementState
            const placementGamesFinished = placementState.numberOfPlacementGamesFinished ?? 0
            const placementGamesRequired = placementState.numberOfPlacementGamesRequired ?? 10
            return {
                description: {key: 'strings:homePage.matchmakingSelect.playPlacementsForRating'},
                badge: {
                    text: {key: 'strings:colonistCommon.utils.#buffer', options: {value: `${placementGamesFinished}/${placementGamesRequired}`}},
                    icon: LobbyImage.IconSwords,
                },
            }
        } else {
            const rankedMMRState = rankedState as RankedMMRState
            const currentMMR = rankedMMRState.currentMMR ?? 0
            const divisionType = rankedMMRState.currentDivisionState.divisionType
            const divisionIcon = UIImagePathController.getRankedDivisionImage(divisionType)
            return {
                description: undefined,
                badge: {
                    text: {key: 'strings:colonistCommon.utils.#buffer', options: {value: Math.round(currentMMR)}},
                    icon: divisionIcon,
                },
            }
        }
    }

    return {description: undefined, badge: undefined}
}

function getShuffleQueueDetails(): RankedMatchTypeData {
    if(ClientUserState.userState == undefined || !ClientUserState.userState.isLoggedIn) {
        return {
            description: {key: 'strings:homePage.unranked.logInToPlayShuffle'},
            badge: undefined,
        }
    }

    const requiredGamesForShuffle = RankedConstants.NumberOfGamesToUnlockRanked
    const completedGames = ClientUserState.userState.totalCompletedGameCount
    if(completedGames < requiredGamesForShuffle) {
        return {
            description: {key: 'strings:homePage.matchmakingSelect.playGamesToUnlockShuffle'},
            badge: {
                text: {key: 'strings:colonistCommon.utils.#buffer', options: {value: `${completedGames}/${requiredGamesForShuffle}`}},
                icon: LobbyImage.IconSwords,
            },
        }
    }

    const queueData = ClientShuffleQueueController.shuffleQueueData
    const playerData = ClientShuffleQueueController.shufflePlayerData
    if(queueData == undefined) {
        return {description: undefined, badge: undefined}
    }

    if(ClientUserState.userState.items.hasMembership() || playerData == undefined) {
        return {description: getShuffleQueueMapAndModeDescription(queueData), badge: undefined}
    }

    const shuffleGamesPlayed = playerData.totalGameCount
    const freeGamesTotalCount = playerData.freeGamesTotalCount
    if(shuffleGamesPlayed < freeGamesTotalCount) {
        const shuffleGamesLeft = freeGamesTotalCount - shuffleGamesPlayed
        return {
            description: getShuffleQueueMapAndModeDescription(queueData),
            badge: {
                text: {key: 'strings:homePage.matchmakingSelect.freeGamesLeft', options: {count: shuffleGamesLeft}},
                icon: LobbyImage.StoreGiftMembership,
            },
        }
    }

    if(!playerData.playedToday) {
        return {
            description: getShuffleQueueMapAndModeDescription(queueData),
            badge: {
                text: {key: 'strings:homePage.matchmakingSelect.dailyFreeShuffleGame'},
                icon: LobbyImage.IconTimeBomb,
                backgroundColorClass: 'mm-badge-yellow',
            },
        }
    }

    return {
        description: getShuffleQueueMapAndModeDescription(queueData),
        badge: {
            text: {key: 'strings:homePage.matchmakingSelect.dailyFreeShuffleGameSubscribe'},
            icon: LobbyImage.StoreMembershipPlus,
        },
    }
}

export function getShuffleQueueMapAndModeDescription(queueData: ShuffleQueueData): TranslationObject {
    const mapName = getShuffleQueueMapName(queueData.mapId)
    const modeName = getShuffleQueueModeName(queueData.modeId)
    const isBotGame = queueData.isBotGame

    if(!mapName) return {key: 'strings:homePage.shuffleQueue.mapAndModeUnknown'}
    if(!modeName) return getNestedTranslation({key: 'strings:homePage.shuffleQueue.mapOnly', options: {context: isBotGame == true ? 'withBots' : ''}}, {map: mapName})

    return getNestedTranslation({key: 'strings:homePage.shuffleQueue.mapAndMode', options: {context: isBotGame == true ? 'withBots' : ''}}, {map: mapName, mode: modeName})
}

export function showMatchmakingErrorPopup(error: BaseError) {
    const title: TranslationObject = error.title
    const errorMessage: TranslationObject = error.description
    const checkAction = () => undefined
    const xAction = () => undefined
    const canCancel = true
    new ViewPopup(title, errorMessage, checkAction, xAction, canCancel, {showXButton: false}).show()
}

export function getClientCKRankedState(): CKRankedState {
    return getCKRankedState(ClientUserState.userState?.items, ClientUserState.userState?.ckTotalGameCount, ClientUserState.abTests, ClientUserState.userState?.ckNextRerollAt, ClientUserState.userState?.ckFreeGameUntil)
}

function getCKRankedCardData(): RankedMatchTypeData | undefined {
    const ckRankedState = getClientCKRankedState()

    switch(ckRankedState) {
        case CKRankedState.Locked:
            return {
                description: {key: 'strings:homePage.becomeAMemberToUnlock'},
                badge: undefined,
            }
        case CKRankedState.HasFreeGames:
            return {
                description: undefined,
                badge: {
                    text: {key: 'strings:homePage.matchmakingSelect.freeGamesLeft', options: {count: CK_FREE_GAME_COUNT - (ClientUserState.userState?.ckTotalGameCount ?? 0)}},
                    icon: LobbyImage.StoreGiftMembership,
                },
            }
        case CKRankedState.HasFreeGameFromDiceRoll:
            const timeUntilFreePlay = TimeUtils.formatCountdownString(ClientUserState.userState?.ckFreeGameUntil as Date)
            return {
                description: undefined,
                badge: {
                    text: {key: 'strings:homePage.matchmakingSelect.freeGameFromDiceRoll', options: {time: timeUntilFreePlay}},
                    icon: LobbyImage.StoreGiftMembership,
                },
            }
        case CKRankedState.CanRollDice:
            return {
                description: undefined,
                badge: {
                    text: {key: 'strings:homePage.matchmakingSelect.dailyFreeRoll'},
                    icon: LobbyImage.Dice6,
                },
            }
        case CKRankedState.WaitingToRollDice:
            const timeUntilDiceRoll = TimeUtils.formatCountdownString(ClientUserState.userState?.ckNextRerollAt as Date)
            return {
                description: undefined,
                badge: {
                    text: {key: 'strings:homePage.matchmakingSelect.dailyFreeRollWaiting', options: {time: timeUntilDiceRoll}},
                    icon: LobbyImage.Dice6,
                },
            }
        default: return undefined
    }
}

function getDescriptionForRankedMatchType(selectedMatchType: MatchmakingMatchTypes, findGameRankedStates?: FindGameRankedState[]): TranslationObject {
    const expectedEloType = rankedMatchTypeToEloTypeMap[selectedMatchType]

    const findGameRankedState = findGameRankedStates?.find((gameState) => gameState.eloType === expectedEloType)
    if(findGameRankedState?.rankedState == undefined) return {key: 'strings:homePage.matchmakingPopup.playAgainstSimilarRating'}

    const rankedState = RankedUserStateController.deserialize(findGameRankedState.rankedState)
    return rankedState.getFindGamePopupStatusText()
}

function isCitiesAndKnightsMatchType(selectedMatchType: MatchmakingMatchTypes): boolean {
    return citiesAndKnightsMatchmakingMatchTypes.has(selectedMatchType as MatchmakingMatchTypes)
}

export function isRankedMatchType(selectedMatchType: MatchmakingMatchTypes): boolean {
    return rankedMatchmakingMatchTypes.has(selectedMatchType as MatchmakingMatchTypes)
}

export function hasFinishedPlacementGamesForSelectedMatchType(selectedMatchType: MatchmakingMatchTypes, findGameRankedStates: FindGameRankedState[]): boolean {
    const expectedEloType = rankedMatchTypeToEloTypeMap[selectedMatchType]
    if(expectedEloType == undefined) return false

    const findGameRankedState = findGameRankedStates.find((gameState) => gameState.eloType == expectedEloType)
    if(findGameRankedState?.rankedState == undefined) return false

    const rankedState = RankedUserStateController.deserialize(findGameRankedState.rankedState)

    if(rankedState.type == RankedUserStateType.Placement) {
        const placementState = rankedState as RankedPlacementState
        return placementState.finishedPlacementGames()
    }

    return true
}

export function shouldShowVideoAdBeforeEnteringRanked(selectedMatchType: MatchmakingMatchTypes, findGameRankedStates: FindGameRankedState[]): boolean {
    if(!UIUtilsEjsVariables.rankedPrerollAdsEnabled) return false
    if(UIUtilsEjsVariables.isUserOnMobile) return false
    if(UIUtilsEjsVariables.isUserOnDiscord) return false

    if(!isRankedMatchType(selectedMatchType)) return false
    if(ClientUserState.userState?.items.hideAds()) return false

    return hasFinishedPlacementGamesForSelectedMatchType(selectedMatchType, findGameRankedStates)
}

const matchTypeToStringMap: Partial<Record<MatchmakingMatchTypes, string>> = {
    [MatchmakingMatchTypes.UnrankedClassic4P]: 'UnrankedClassic4P',
    [MatchmakingMatchTypes.RankedClassic4P]: 'RankedClassic4P',
    [MatchmakingMatchTypes.UnrankedClassic1v1]: 'UnrankedClassic1v1',
    [MatchmakingMatchTypes.RankedClassic1v1]: 'RankedClassic1v1',
    [MatchmakingMatchTypes.UnrankedCitiesAndKnights4P]: 'UnrankedCitiesAndKnights4P',
    [MatchmakingMatchTypes.RankedCitiesAndKnights4P]: 'RankedCitiesAndKnights4P',
    [MatchmakingMatchTypes.UnrankedShuffle]: 'UnrankedShuffle',
}

export function getGameModeStringForAnalytics(matchTypeOrBotDifficulty: MatchmakingMatchTypes | string): string {
    if(typeof matchTypeOrBotDifficulty === 'string') {
        const difficulty = matchTypeOrBotDifficulty.charAt(0).toUpperCase() + matchTypeOrBotDifficulty.slice(1).toLowerCase()
        return `PlayVsBots_${difficulty}`
    }

    return matchTypeToStringMap[matchTypeOrBotDifficulty] ?? 'Unknown'
}

export function trackMatchmakingTimeToQueueStart(componentInitTimestamp: number, gameModeString: string) {
    if(!UIUtilsEjsVariables.isUserOnMobileAppOrWeb) return
    const timeToClickInSeconds = (Date.now() - componentInitTimestamp) / 1000

    void CA.sendAnalyticEvent({
        type: AnalyticEventType.MatchmakingUITimeToFirstQueueStart,
        category: AnalyticEventCategory.Matchmaking,
        floatValue: timeToClickInSeconds,
        stringValue: gameModeString,
    } as AjaxDataTypes.AnalyticData)
}

export function isBotMatchSelected(matchType: MatchType): boolean {
    return Object.values(BotsMatchType).includes(matchType as BotsMatchType)
}

export function startBotGame(botMatchType: BotsMatchType) {
    switch(botMatchType) {
        case BotsMatchType.BeginnerMode:
            void SocketLobbySend.continueCampaignMode()
            break
        case BotsMatchType.ColonistRush:
            void SocketLobbySend.clickedStartColonistRushBotGame()
            break
        case BotsMatchType.Easy:
            CA.sendGoogleAnalyticsEvent(CA.Category.LandingPage, CA.Label.ClickedLandingPageVSBots)
            void SocketLobbySend.clickedStartEasyBotGame()
            break
        case BotsMatchType.Medium:
            CA.sendGoogleAnalyticsEvent(CA.Category.LandingPage, CA.Label.ClickedLandingPageVSBots)
            void SocketLobbySend.clickedStartMediumBotGame()
            break
        case BotsMatchType.Hard:
            CA.sendGoogleAnalyticsEvent(CA.Category.LandingPage, CA.Label.ClickedLandingPageVSBots)
            void SocketLobbySend.clickedStartHardBotGame()
            break
    }
}

export function isShuffleBotGame(matchType: MatchmakingMatchTypes): boolean {
    if(matchType != MatchmakingMatchTypes.UnrankedShuffle) return false
    return ClientShuffleQueueController.shuffleQueueData?.isBotGame == true
}

export function startShuffleBotGame() {
    void SocketLobbySend.startShuffleBotGame()
}
